<script setup lang="ts">
import { defineProps, ref } from "vue";

import CourseLearn from "@/components/common/CourseLearn.vue";
import CoursePerks from "@/components/common/CoursePerks.vue";
import Reviews from "@/components/common/Reviews.vue";
import Block from "@/components/ui/atoms/Block.vue";
import BlockContainer from "@/components/ui/atoms/BlockContainer.vue";
import Btn from "@/components/ui/atoms/Btn.vue";
import MasterBlock from "@/components/ui/atoms/MasterBlock.vue";
import CourseCard from "@/components/ui/molecules/CourseCard.vue";
import FullVideo from "@/components/ui/molecules/FullVideo.vue";
import ProductPriceCard from "@/components/ui/molecules/ProductPriceCard.vue";
import MainLayout from "@/layouts/MainLayout.vue";
import type { ProductPrice } from "@/shared/checkoutTypes";
import type { Branch, Chapter, Course, Review, Lesson } from "@/shared/explorerTypes";
import { resolveUrl } from "@/utils/urls";

interface Props {
  course: Course;
  chapters: Chapter[];
  recommendedCourses: Course[];
  nextCourses: Course[];
  reviews: Review[];
  productPrices: ProductPrice[];
  currentPrice: ProductPrice;
  currentBranch: Branch;
  previewVideo: string;
}

const props = defineProps<Props>();

const openChapters = ref<number[]>([]);
const showFullVideo = ref(false);

const toggleChapter = (id: number) => {
  const index = openChapters.value.indexOf(id);
  if (index === -1) {
    openChapters.value.push(id);
    return;
  }
  openChapters.value.splice(index, 1);
};

const openDosier = () => {
  window.open(props.course.dosier, "_blank");
};

const changeSection = (id: string) => {
  const element = document.querySelector(`#${id}`);
  if (!element) return;
  element.scrollIntoView({ behavior: "smooth", block: "start" });
};

const showSubjectTrailer = (lesson: Lesson) => {
  console.log(lesson);
  showFullVideo.value = true;
};
</script>

<template>
  <MainLayout>
    <MasterBlock theme="smoke">
      <section :class="'header container'">
        <Block
          class="header__image clear"
          :style="`${
            course.poster_image
              ? 'background-image: url(' + course.poster_image + ')'
              : ''
          }`"
        />
        <Block class="header__info" theme="clear">
          <h1 :class="['size-xl']">{{ course.name }}</h1>
          <div>
            <p>{{ course.description }}</p>
            <Btn theme="dark-bold" shape="round" @click="changeSection('precios')">
              Acceder al curso
            </Btn>
            <div class="header__info__blocks">
              <Block theme="dust" shape="square" :class="['header__info__blocks__item']">
                <span class="header__info__blocks__item__title size-xs no-margins">
                  RAMA
                </span>
                <h3 class="header__info__blocks__item__value no-margins">
                  {{ currentBranch.title }}
                </h3>
              </Block>
              <Block theme="dust" shape="square" :class="['header__info__blocks__item']">
                <span class="header__info__blocks__item__title size-xs no-margins">
                  DURACIÓN
                </span>
                <h3 class="header__info__blocks__item__value no-margins">
                  {{ course.duration_in_hours }} horas
                </h3>
              </Block>
              <Block theme="dust" shape="square" :class="['header__info__blocks__item']">
                <span class="header__info__blocks__item__title size-xs no-margins">
                  DURACIÓN EN PRÁCTICA
                </span>
                <h3 class="header__info__blocks__item__value no-margins">
                  {{ course.estimated_time }} horas
                </h3>
              </Block>
              <Block theme="dust" shape="square" :class="['header__info__blocks__item']">
                <span class="header__info__blocks__item__title size-xs no-margins">
                  NIVEL
                </span>
                <h3 class="header__info__blocks__item__value no-margins">
                  {{ course.level_text }}
                </h3>
              </Block>
            </div>
          </div>
        </Block>
      </section>
    </MasterBlock>

    <CourseLearn :course="course" :currentBranch="currentBranch" container="container" />

    <MasterBlock theme="cloud" :style="`background-color: ${currentBranch.base_color}`">
      <section class="container">
        <div class="container__header">
          <h2 :style="`color: ${currentBranch.text_color}`">Temario</h2>
          <Btn
            theme="clear-wireframe"
            shape="round"
            size="small"
            @click="openDosier"
            v-if="course.type === 'course'"
          >
            Ver ficha técnica
          </Btn>
        </div>

        <Block theme="clear">
          <Block
            theme="off-white"
            v-for="chapter in chapters"
            :key="chapter.id"
            class="chapter-container"
          >
            <div class="chapter-container__title" @click="toggleChapter(chapter.id)">
              <h4>{{ chapter.name }}</h4>
              <span v-if="!openChapters.includes(chapter.id)">
                <img src="@img/icons/collapse-open.svg" alt="Capítulo expandido" />
              </span>
              <span v-else>
                <img src="@img/icons/collapse-close.svg" alt="Capítulo cerrado" />
              </span>
            </div>
            <div
              v-if="openChapters.includes(chapter.id)"
              class="chapter-container__lessons"
            >
              <ul>
                <Block
                  tag="li"
                  theme="dust"
                  v-for="lesson in chapter.lessons"
                  :key="lesson.id"
                >
                  {{ lesson.name }}
                  <Btn
                    v-if="lesson.is_preview"
                    shape="round"
                    theme="dark-wireframe"
                    class="sizeless"
                    @click="showSubjectTrailer(lesson)"
                  >
                    Ver lección
                  </Btn>
                </Block>
              </ul>
            </div>
          </Block>
        </Block>
      </section>
    </MasterBlock>

    <FullVideo
      :src="previewVideo"
      :is-visible="showFullVideo"
      @close="showFullVideo = false"
    />

    <CoursePerks :course="course" :current-branch="currentBranch" />

    <MasterBlock
      theme="iron"
      v-if="nextCourses.length > 0 || recommendedCourses.length > 0"
    >
      <section class="container">
        <div v-if="recommendedCourses.length > 0">
          <div class="container__header">
            <h2>ANTES DE ESTE DEBERÍAS HACER...</h2>
          </div>
          <BlockContainer theme="iron" behaviour="scroll" :class="['courses-list']">
            <CourseCard
              :course="course"
              light
              v-for="course in recommendedCourses"
              :key="course.id"
              :class="['short-course']"
            />
          </BlockContainer>
          <div class="divider" />
        </div>
        <div v-if="nextCourses.length > 0">
          <div class="container__header">
            <h2>CURSOS QUE PUEDES HACER A CONTINUACIÓN</h2>
          </div>

          <BlockContainer theme="iron" behaviour="scroll" :class="['courses-list']">
            <CourseCard
              :course="course"
              light
              v-for="course in nextCourses"
              :key="course.id"
              :class="['short-course']"
            />
          </BlockContainer>
        </div>
      </section>
    </MasterBlock>

    <MasterBlock
      theme="off-white"
      v-if="reviews.length > 0"
      class="anchored"
      id="opiniones"
    >
      <section class="container">
        <div class="container__header">
          <h2>¿QUE DICEN NUESTROS ALUMNOS?</h2>
        </div>
        <Reviews class="reviews" :reviews="reviews" />
      </section>
    </MasterBlock>

    <MasterBlock theme="off-black" id="precios">
      <section class="container">
        <div class="container__header">
          <h2>SUSCRÍBETE A ACCEDE A TODO NUESTRO CONTENIDO</h2>
        </div>
        <BlockContainer behaviour="stripes" :class="['pricing-container']">
          <ProductPriceCard
            v-for="productPrice in productPrices"
            :productPrice="productPrice"
            :class="['price-card']"
            :key="productPrice.id"
          />
        </BlockContainer>
        <div class="divider" />
        <h3>¿QUÉ INCLUYE LA SUSCRIPCIÓN?</h3>
        <ul>
          <li>Acceso a más de 70 cursos sobre tecnología</li>
          <li>Ejercicios y proyectos corregidos por profesores reales</li>
          <li>Soporte personalizado por parte de nuestros profesores</li>
          <li>Itinerario de aprendizaje guiado</li>
          <li>
            Acceso a foro privado en cada curso para resolver dudas y compartir
            experiencias
          </li>
          <li>Certificados por superar cada curso</li>
        </ul>

        <div class="pricing-separator" />
        <h2>COMPRAR ÚNICAMENTE ESTE CONTENIDO</h2>
        <Block theme="dust" class="single-price">
          <div class="single-price__title">
            <span class="size-xl ff-apparat-medium text-uppercase">Acceso al curso</span>
            <div class="single-price__discount" v-if="currentPrice.price_off">
              <span class="size-h1">{{ currentPrice.price_off }}€</span>
              <span class="remove ff-apparat-medium size-xl">
                {{ currentPrice.price }}€
              </span>
            </div>
            <span class="size-h1" v-else>{{ currentPrice.price }}€</span>
          </div>
          <div class="single-price__cta">
            <h3>
              ACCESO VITALICIO AL CURSO
              <br />
              Y TODAS SUS ACTUALIZACIONES
            </h3>
            <Btn
              tag="a"
              theme="dark-bold"
              shape="round"
              :href="resolveUrl('order_product_add', currentPrice.product)"
            >
              Comprar
            </Btn>
          </div>
        </Block>

        <div class="divider" />
        <h3>¿QUÉ INCLUYE LA COMPRA?</h3>
        <ul>
          <li>
            Acceso vitalicio al curso y todas sus futuras actualizaciones, así como nuevo
            contenido que se añada en el futuro
          </li>
          <li>Ejercicios y proyectos corregidos por profesores reales</li>
          <li>
            Acceso a foro privado en cada curso para resolver dudas y compartir
            experiencias
          </li>
          <li>Certificados al aprobar el curso</li>
        </ul>
      </section>
    </MasterBlock>
  </MainLayout>
</template>

<style scoped lang="scss">
@use "@/styles/settings/vars";

.divider {
  margin-bottom: 2em;
}

.container {
  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;

    button {
      text-transform: uppercase;
    }

    h2 {
      margin: 0;
    }
  }
}

.header {
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 1em;

  &__image {
    width: 100%;
    object-fit: cover;
    background-size: cover;
    margin-bottom: 0;
  }

  &__info {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 0;

    h1 {
      font-size: 44px;
      font-weight: 600;
    }

    &__blocks {
      margin-top: 1em;
      display: grid;
      grid-template-columns: repeat(4, minmax(0, 1fr));
      gap: 1em;

      &__item {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        padding: 0.5em;
        gap: 1em;
        border-radius: 0.5em;
      }

      span {
        color: vars.$off-black;
        padding: 0;
      }

      h3 {
        text-transform: uppercase;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .block {
        flex: 1;
        margin-bottom: 0;
      }
    }
  }
}

.summary {
  background-color: vars.$agile-blue;

  h2 {
    color: vars.$off-white;
  }
}

.chapter-container {
  &__title {
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;

    h4 {
      margin: 0;
      text-transform: uppercase;
    }
  }

  &__lessons {
    ul {
      list-style: none;
      margin: 0;
      padding: 0;
    }

    li {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 0.5em;
    }

    margin-top: 1em;

    .block {
      padding: 0.75em;
      border-radius: 6px;
    }
  }
}

.short-course {
  max-width: 550px !important;
}

.pricing-separator {
  margin: 2em 0;
  width: 100%;
  height: 2px;
  background-color: vars.$poly-fuchsia;
}

.single-price {
  display: flex;
  justify-content: space-between;

  &__title {
    display: flex;
    flex-direction: column;
    justify-content: center;

    .size-h1 {
      margin-bottom: 0;
    }
  }

  &__discount {
    display: flex;
    align-items: start;
    gap: 0.5em;
  }

  &__cta {
    h3 {
      margin: 0;
      font-weight: 400;
      text-align: right;
    }

    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-end;
    gap: 1em;
  }

  @media (max-width: 600px) {
    flex-direction: column;
    &__title {
      span {
      }

      h3 {
        text-align: left;
      }

      .size-h1 {
        margin-bottom: 4px;
      }
    }

    &__cta {
      h3 {
        align-self: flex-start;
        text-align: left;
      }
    }
  }
}

@media (width < 600px) {
  .header__info__blocks {
    grid-template-columns: 1fr 1fr;
  }

  .container.header {
    gap: 0;
    grid-template-columns: 1fr;
  }

  .header {
    h1 {
      font-size: 32px;
      line-height: 36px;
      margin-bottom: 0;
    }
  }

  .container.header .header__image {
    height: 200px;
  }
}

li {
  margin-bottom: 4px;
}
</style>
